
import { Injectable, signal } from '@angular/core';

export type PortalThemeMode = 'light' | 'dark';

const LIGHT_VARS: Record<string, string> = {
  '--surface': '#FFFFFF',
  '--canvas': '#F4F6F8',

  '--text': '#1D2D3E',
  '--textSecondary': '#556B82',
  '--textTertiary': '#52677C',

  '--border': '#E4E9EE',
  '--borderStrong': '#D3DBE3',
  '--neutralTint': '#F0F3F6',

  // Darker blue for text and navigation.
  // Contrast on #EAF1FD: approximately 6.64:1.
  '--brand': '#0053A6',
  '--brandTint': '#EAF1FD',

  // Dedicated accessible link color.
  '--linkColor': '#0053A6',

  // Keep PrimeNG action/button branding unchanged.
  '--p-primary-color': '#0053A6',
  '--p-primary-contrast-color': '#FFFFFF',

  '--p-button-primary-background': '#0053A6',
  '--p-button-primary-hover-background': '#0053A6',
  '--p-button-primary-active-background': '#0053A6',

  '--p-button-primary-border-color': '#0053A6',
  '--p-button-primary-hover-border-color': '#0053A6',
  '--p-button-primary-active-border-color': '#0053A6',

  '--p-button-primary-color': '#FFFFFF',
  '--p-button-primary-hover-color': '#FFFFFF',
  '--p-button-primary-active-color': '#FFFFFF',

  '--p-form-field-focus-border-color': '#0053A6',

  '--p-highlight-background': '#EAF1FD',
  '--p-highlight-color': '#0053A6',

  '--p-tabs-tab-active-color': '#0053A6',
  '--p-tabs-tab-active-border-color': '#0053A6',

  '--p-datepicker-panel-background': '#FFFFFF',
  '--p-datepicker-panel-color': '#1D2D3E',
  '--p-datepicker-panel-border-color': '#E4E9EE',

  '--p-datepicker-date-color': '#1D2D3E',
  '--p-datepicker-date-hover-background': '#EAF1FD',
  '--p-datepicker-date-hover-color': '#0053A6',

  '--p-datepicker-date-selected-background': '#0053A6',
  '--p-datepicker-date-selected-color': '#FFFFFF',

  // Message text colours of the theme are below 4.5:1 on their light backgrounds (warn 2.8, error 4.4, success 3.2).
  '--p-message-warn-color': '#7A4B00',
  '--p-message-error-color': '#B42318',
  '--p-message-success-color': '#166534',

  // The same for the pop-up messages (toasts): the theme's green is 3.3:1 on its light green.
  '--p-toast-info-color': '#1D4ED8',
  '--p-toast-warn-color': '#7A4B00',
  '--p-toast-error-color': '#B42318',
  '--p-toast-success-color': '#166534'
};

const DARK_VARS: Record<string, string> = {
  '--surface': '#1A2029',
  '--canvas': '#12171D',

  '--text': '#EAF0F6',
  '--textSecondary': '#9FB0C2',
  '--textTertiary': '#B3C0CE',

  '--border': '#2A323C',
  '--borderStrong': '#39424D',
  '--neutralTint': '#20272F',

  '--brand': '#4DA3F5',
  '--brandTint': '#1C2C40',

  '--linkColor': '#93C5FD',

  '--p-primary-color': '#0053A6',
  '--p-primary-contrast-color': '#FFFFFF',

  '--p-button-primary-background': '#0053A6',
  '--p-button-primary-hover-background': '#0053A6',
  '--p-button-primary-active-background': '#0053A6',

  '--p-button-primary-border-color': '#0053A6',
  '--p-button-primary-hover-border-color': '#0053A6',
  '--p-button-primary-active-border-color': '#0053A6',

  '--p-button-primary-color': '#FFFFFF',
  '--p-button-primary-hover-color': '#FFFFFF',
  '--p-button-primary-active-color': '#FFFFFF',

  '--p-form-field-focus-border-color': '#4DA3F5',

  '--p-highlight-background': '#1C2C40',
  '--p-highlight-color': '#93C5FD',

  '--p-tabs-tab-active-color': '#4DA3F5',
  '--p-tabs-tab-active-border-color': '#4DA3F5',

  '--p-datepicker-panel-background': '#1A2029',
  '--p-datepicker-panel-color': '#EAF0F6',
  '--p-datepicker-panel-border-color': '#2A323C',

  '--p-datepicker-date-color': '#EAF0F6',
  '--p-datepicker-date-hover-background': '#1C2C40',
  '--p-datepicker-date-hover-color': '#93C5FD',

  '--p-datepicker-date-selected-background': '#0053A6',
  '--p-datepicker-date-selected-color': '#FFFFFF',

  // Outlined/text/link buttons keep the dark primary on a dark surface (about 2:1), so they use the light brand colour.
  '--p-button-outlined-primary-color': '#4DA3F5',
  '--p-button-outlined-primary-border-color': '#4DA3F5',
  '--p-button-text-primary-color': '#4DA3F5',
  '--p-button-link-color': '#93C5FD',
  '--p-button-link-hover-color': '#BFDBFE',

  // Message text on the dark translucent backgrounds.
  '--p-message-info-color': '#93C5FD',
  '--p-message-warn-color': '#FCD34D',
  '--p-message-error-color': '#FCA5A5',
  '--p-message-success-color': '#86EFAC',

  '--p-toast-info-color': '#93C5FD',
  '--p-toast-warn-color': '#FCD34D',
  '--p-toast-error-color': '#FCA5A5',
  '--p-toast-success-color': '#86EFAC'
};

@Injectable({
  providedIn: 'root'
})
export class PortalThemeService {
  readonly mode = signal<PortalThemeMode>('light');

  constructor() {
    this.apply('light');
  }

  toggle(): void {
    this.apply(
      this.mode() === 'light' ? 'dark' : 'light'
    );
  }

  private apply(mode: PortalThemeMode): void {
    this.mode.set(mode);

    const variables =
      mode === 'light' ? LIGHT_VARS : DARK_VARS;

    const root = document.documentElement;

    // Variables that only the other theme defines must not leak into this one.
    const other = mode === 'light' ? DARK_VARS : LIGHT_VARS;
    for (const key of Object.keys(other)) {
      if (!(key in variables)) root.style.removeProperty(key);
    }

    for (const [key, value] of Object.entries(variables)) {
      root.style.setProperty(key, value);
    }

    root.setAttribute('data-theme', mode);
    root.style.colorScheme = mode;

    document.body.style.setProperty(
      '--portal-body-background',
      variables['--canvas']
    );

    document.body.style.setProperty(
      'background-color',
      variables['--canvas'],
      'important'
    );

    document.body.style.setProperty(
      'color',
      variables['--text'],
      'important'
    );
  }
}